import { Box, Drawer, Modal, useMediaQuery } from '@mui/material';
import { Decimal } from 'decimal.js';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { BasketProductDto } from 'src/api/basketService/dto/BasketProduct.dto';
import { ReactComponent as CloseIcon } from 'src/assets/svg/close-icon.svg';
import { routes } from 'src/constants/routes';
import { useGetIsValidRebooker } from 'src/lib/hooks/use-get-is-valid-rebooker';
import { useUpdateBasket } from 'src/lib/hooks/use-patch-update-basket';
import { Currency } from 'src/shared/enum/Currency';
import { ErrorCode } from 'src/shared/enum/ErrorCode';
import { ValidationErrors } from 'src/shared/enum/ValidationErrors';
import {
    clearBasket,
    selectBasket,
    selectBasketId,
    setBasket,
    updateBasketOptions,
} from 'src/store/slices/basket-slice';
import { selectBookingDetails, selectRoom } from 'src/store/slices/booking-slice';
import { selectCreatedPaymentPlan, selectUserChoices } from 'src/store/slices/payment-plan-slice';
import { setSnackbarState } from 'src/store/slices/snackbar-slice';
import { theme } from 'src/styles/theme';
import {
    getCbl,
    getPaymentPlanId,
    getPromotionId,
    setBasketStorage,
} from 'src/utils/local-storage-versioning';

import { BookingDetails } from '../booking-summary/booking-details';
import { PaymentPlan } from '../payment-plan';

import { styles } from './styles';

import type { BookingDetailsDialogProps } from './types';

export const BookingDetailsDialog: FC<BookingDetailsDialogProps> = ({ isOpen, handleIsOpen }) => {
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isDrawer = useMediaQuery(theme.breakpoints.down('lg'));
    const bookingDetails = useSelector(selectBookingDetails);
    const currentBasket = useSelector(selectBasket);
    const storedBasketId = useSelector(selectBasketId);
    const roomData = useSelector(selectRoom);
    const paymentPlan = useSelector(selectCreatedPaymentPlan);
    const userChoices = useSelector(selectUserChoices);
    const [promotionId, setPromotionId] = useState<string | null>(null);

    const { mutateAsync: updateBasketAsync } = useUpdateBasket();
    const { data: isValidRebooker, fetchByRoomCode } = useGetIsValidRebooker();

    const onSubmit = async () => {
        if (
            !roomData ||
            !paymentPlan ||
            !paymentPlan.totalRent ||
            !paymentPlan.subtotal ||
            !paymentPlan.paymentOptions ||
            !userChoices.term
        )
            return;

        const cbl = getCbl(searchParams);
        const paymentPlanId = getPaymentPlanId(searchParams);

        const product: BasketProductDto = new BasketProductDto({
            id: roomData.id as string,
            room_code: roomData.room_code as string,
            academic_year: roomData.academic_year as string,
            building_id: roomData.building_id as string,
            development_id: roomData.development_slug as string,
            price: new Decimal(roomData.monthly_price ?? roomData.base_price ?? 0),
            currency: roomData.currency as string,
            deposit_prepaid: roomData.deposit_prepaid,
            advance_payment_weeks: roomData.advance_payment_weeks as number,
            beds: roomData.number_of_beds as number,
            deposit: roomData.deposit_prepaid
                ? new Decimal(0)
                : new Decimal(roomData.base_price).times(roomData.advance_payment_weeks),
            selected_students: userChoices.numberOfStudents as number,
            term: userChoices.term.name,
            payment_plan_id: userChoices.instalmentId as string,
            instalment_type: userChoices.instalmentType as string,
            cbl: !!cbl,
            stl: !!paymentPlanId,
            occupants: currentBasket.room_mates || [],
            selected_price: {
                fee: paymentPlan.weeklyPrice,
                today: paymentPlan.paymentOptions[0].price,
                non_discounted: paymentPlan.subtotal,
            },
            legal_representative: null,
            promotion_id: paymentPlan.promotion ? paymentPlan.promotion.id : null,
        });

        try {
            if (storedBasketId && currentBasket.updated_date) {
                const response = await updateBasketAsync({
                    basketId: storedBasketId,
                    basketData: {
                        ...currentBasket,
                        products: [product],
                    },
                    updatedAt: currentBasket.updated_date,
                });
                dispatch(setBasket(response));
                setBasketStorage(response);
            }
        } catch (err) {
            if (err.response.data.code === ErrorCode.ERROR_BASKET_OUTDATED) {
                dispatch(clearBasket());
                dispatch(setSnackbarState({ message: t('basket_out_of_date_message') }));
                navigate(routes.summary.index);
                return;
            }
            if (err.response.data && err.response.data.data && err.response.data.data.room) {
                if (err.response.data.data.room[0] === ValidationErrors.ROOM_NOT_AVAILABLE) {
                    navigate(routes.roomNotAvailable.index);
                    return;
                }
                if (err.response.data.data.room[0] === ValidationErrors.BASE_PRICE_MISMATCH) {
                    navigate(routes.summary.index);
                    dispatch(setSnackbarState({ message: t('room_price_updated_message') }));
                    return;
                }
            }
        } finally {
            handleIsOpen();
        }
    };

    useEffect(() => {
        const isRebooker = currentBasket.options.rebooker;
        if (roomData && roomData.room_code && isRebooker) {
            fetchByRoomCode(roomData.room_code);
        }
    }, [roomData]);

    useEffect(() => {
        if (isValidRebooker && isValidRebooker.rebooker) {
            setPromotionId(isValidRebooker.promotion_id);
            dispatch(
                updateBasketOptions({
                    rebooker: isValidRebooker.rebooker,
                    rebookerPhase: isValidRebooker.rebookers_stage,
                    fillerWeekEligible: isValidRebooker.filler_week_eligible,
                })
            );
        }
    }, [isValidRebooker]);

    useEffect(() => {
        const pid = getPromotionId(searchParams);

        if (pid) setPromotionId(pid);
    }, []);

    const content = useMemo(() => {
        if (!bookingDetails) return null;
        const currency = bookingDetails.currency as Currency;

        return (
            <Box css={styles.container}>
                <Box css={styles.header}>
                    <CloseIcon css={styles.closeIcon} onClick={onSubmit} />
                </Box>
                <Box css={styles.contentWrapper}>
                    <BookingDetails
                        restStyles={styles.details}
                        bookingDetails={bookingDetails}
                        promotionId={promotionId}
                        isRebooker={currentBasket.options.rebooker}
                        setButtonDisabled={() => {}}
                        isFillerWeekEligible={currentBasket.options.fillerWeekEligible}
                    />
                </Box>
                <Box css={styles.bgGray}>
                    <Box css={styles.contentWrapper}>
                        <PaymentPlan
                            isEditBookingDetailsDialog={isOpen}
                            showPaymentOptions={false}
                            restStyles={styles.payment}
                            ctaLabel="edit_booking_details_cta"
                            onSubmit={onSubmit}
                            currency={currency}
                            disabled={false}
                        />
                    </Box>
                </Box>
            </Box>
        );
    }, [bookingDetails, handleIsOpen, isOpen, userChoices, paymentPlan]);

    if (isDrawer) {
        return (
            <Drawer open={isOpen} anchor="bottom" PaperProps={{ sx: styles.drawer }}>
                {content}
            </Drawer>
        );
    }

    return (
        <Modal open={isOpen} aria-labelledby="booking-details-modal">
            <Box css={styles.modal}>{content}</Box>
        </Modal>
    );
};
